import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { MediaUploader } from "@shared/components/media/MediaUploader";
import { useProfile } from "@shared/hooks/useProfile";

import Button from "@shared/components/ui/Button";
import { MediaFile, MediaType } from "@shared/types/media.types";
import theme from "@shared/utils/themeColors";
import GifPicker, { ContentFilter } from "gif-picker-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { usePostStore } from "../../store/postStore";
import { MediaItem, PostType, PostVisibility } from "../../types/post.types";

export function CreatePost() {
  const { user, getProfileThumbnail } = useProfile();
  const { createPost } = usePostStore();
  const [content, setContent] = useState("");
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [showMediaUploader, setShowMediaUploader] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [isLoadingGif, setIsLoadingGif] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!user) {
    return null;
  }

  const { profile, email } = user;

  const handleSubmit = async () => {
    if (!content.trim() && mediaItems.length === 0) return;

    try {
      setIsSubmitting(true);

      await createPost({
        content: content.trim(),
        type:
          mediaItems.length > 0
            ? mediaItems[0].type === "image"
              ? PostType.IMAGE
              : PostType.VIDEO
            : PostType.TEXT,
        visibility: PostVisibility.PUBLIC,
        media: mediaItems,
      });

      setContent("");
      setMediaItems([]);
      setShowMediaUploader(false);
      setShowGifPicker(false);
    } catch (error) {
      toast.error("Error creating post. Please try again.");
      console.error("Error creating post:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMediaUpload = (files: MediaFile[]) => {
    const newMediaItems: MediaItem[] = files.map((file) => ({
      type: file.type,
      variants: [
        {
          url: file.uploadedUrl!,
          format: file.file.type.split("/")[1] || "unknown",
          type: "original",
        },
      ],
    }));

    setMediaItems([...mediaItems, ...newMediaItems]);
    setShowMediaUploader(false);
  };

  const handleGifSelect = async (gif: { url: string }) => {
    try {
      setIsLoadingGif(true);

      // Create MediaItem for GIF
      const newMediaItem: MediaItem = {
        type: MediaType.IMAGE,
        variants: [
          {
            url: gif.url,
            type: "original",
            format: "gif",
          },
        ],
      };

      // Update state
      setMediaItems([newMediaItem]);
      setShowGifPicker(false);
    } catch (error) {
      console.error("Error selecting GIF:", error);
      toast.error("Error al seleccionar el GIF");
    } finally {
      setIsLoadingGif(false);
    }
  };

  const canAddMedia =
    mediaItems.length === 0 ||
    (mediaItems[0].type === MediaType.IMAGE &&
      mediaItems[0].variants[0].format !== "gif");

  const canAddGif = mediaItems.length === 0;

  return (
    <div className={`${theme.bg.primary} shadow-sm rounded-xl p-5`}>
      {/* User Input Area */}
      <div className="flex space-x-4">
        <img
          src={getProfileThumbnail(profile)}
          alt="User"
          className="w-10 h-10 rounded-full"
        />
        <div className="grow">
          <label htmlFor="status-input" className="sr-only">
            What's happening, {profile?.name?.trim()?.split(" ")[0]}?
          </label>
          <input
            id="status-input"
            className="form-input w-full bg-gray-100 dark:bg-gray-700 border-transparent dark:border-transparent focus:bg-white dark:focus:bg-gray-800 placeholder-gray-500"
            type="text"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={`What's on your mind, ${
              profile?.name?.trim()?.split(" ")[0]
            }?`}
          />
        </div>
      </div>

      {/* Media Preview Grid */}
      {mediaItems.length > 0 && (
        <div className="mb-5 grid grid-cols-2 gap-4 mt-4">
          {mediaItems.map((item, index) => (
            <div key={index} className="relative">
              {item.type === "image" ? (
                <img
                  src={item.variants[0].url}
                  alt="Media preview"
                  className="w-full h-32 object-cover rounded-lg"
                />
              ) : (
                <video
                  src={item.variants[0].url}
                  className="w-full h-32 object-cover rounded-lg"
                />
              )}
              <button
                onClick={() =>
                  setMediaItems((items) => items.filter((_, i) => i !== index))
                }
                className={`absolute -top-2 -right-2 ${theme.bg.error} ${theme.bg.errorHover} text-white rounded-full p-1`}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Media Uploader */}
      {showMediaUploader && (
        <div className="mb-5">
          <MediaUploader
            onUploadComplete={handleMediaUpload}
            onError={(error) => {
              toast.error(error);
              setShowMediaUploader(false);
            }}
            maxFiles={4}
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between items-center mt-4">
        <div className="flex space-x-4">
          {/* Media Upload Button */}
          {canAddMedia && (
            <button
              type="button"
              onClick={() => {
                setShowMediaUploader(!showMediaUploader);
                setShowGifPicker(false);
                setShowEmojiPicker(false);
              }}
              className="inline-flex items-center text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200"
              disabled={isSubmitting}
            >
              <svg
                className={`${theme.bg.fill} mr-2`}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M0 0h2v16H0V0Zm14 0h2v16h-2V0Zm-3 7H5c-.6 0-1-.4-1-1V1c0-.6.4-1 1-1h6c.6 0 1 .4 1 1v5c0 .6-.4 1-1 1ZM6 5h4V2H6v3Zm5 11H5c-.6 0-1-.4-1-1v-5c0-.6.4-1 1-1h6c.6 0 1 .4 1 1v5c0 .6-.4 1-1 1Zm-5-2h4v-3H6v3Z" />
              </svg>
              <span>Media</span>
            </button>
          )}

          {/* GIF Button */}
          {canAddGif && (
            <button
              type="button"
              onClick={() => {
                setShowGifPicker(!showGifPicker);
                setShowMediaUploader(false);
                setShowEmojiPicker(false);
              }}
              className="inline-flex items-center text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200"
              disabled={isSubmitting}
            >
              <svg
                className={`${theme.bg.fill} mr-2`}
                width="16"
                height="16"
                viewBox="0 0 16 16"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M6.974 14c-.3 0-.7-.2-.9-.5l-2.2-3.7-2.1 2.8c-.3.4-1 .5-1.4.2-.4-.3-.5-1-.2-1.4l3-4c.2-.3.5-.4.9-.4.3 0 .6.2.8.5l2 3.3 3.3-8.1c0-.4.4-.7.8-.7s.8.2.9.6l4 8c.2.5 0 1.1-.4 1.3-.5.2-1.1 0-1.3-.4l-3-6-3.2 7.9c-.2.4-.6.6-1 .6Z" />
              </svg>
              <span>GIF</span>
            </button>
          )}

          {/* Emoji Button */}
          <button
            type="button"
            onClick={() => {
              setShowEmojiPicker(!showEmojiPicker);
              setShowGifPicker(false);
              setShowMediaUploader(false);
            }}
            className="inline-flex items-center text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200"
            disabled={isSubmitting}
          >
            <svg
              className={`${theme.bg.fill} mr-2`}
              width="16"
              height="16"
              viewBox="0 0 16 16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M9.793 10.002a.5.5 0 0 1 .353.853l-1.792 1.793a.5.5 0 0 1-.708 0l-1.792-1.793a.5.5 0 0 1 .353-.853h3.586Zm5.014-4.63c1.178 2.497 1.833 5.647.258 7.928-1.238 1.793-3.615 2.702-7.065 2.702S2.173 15.092.935 13.3c-1.575-2.28-.92-5.431.258-7.927A2.962 2.962 0 0 1 0 3.002a3 3 0 0 1 3-3c.787 0 1.496.309 2.029.806a5.866 5.866 0 0 1 5.942 0A2.96 2.96 0 0 1 13 .002a3 3 0 0 1 3 3c0 .974-.472 1.827-1.193 2.37Zm-1.387 6.79c1.05-1.522.417-3.835-.055-5.078C12.915 5.89 11.192 2.002 8 2.002s-4.914 3.89-5.365 5.082c-.472 1.243-1.106 3.556-.055 5.079.843 1.22 2.666 1.839 5.42 1.839s4.577-.62 5.42-1.84ZM6.67 6.62c.113.443.102.68-.433 1.442-.535.761-1.06 1.297-1.658 1.297-.597 0-1.08-.772-1.07-1.483.01-.71.916-2.306 1.997-2.306.784 0 1.05.607 1.163 1.05Zm3.824-1.05c1.08 0 1.987 1.596 1.997 2.306.01.71-.473 1.483-1.07 1.483-.598 0-1.123-.536-1.658-1.297-.535-.762-.546-1-.432-1.442.113-.443.38-1.05 1.163-1.05Z" />
            </svg>
            <span>Emoji</span>
          </button>
        </div>

        {/* Post Button */}
        <Button
          onClick={handleSubmit}
          disabled={
            isSubmitting || (!content.trim() && mediaItems.length === 0)
          }
        >
          {isSubmitting ? "Posting..." : "Post"}
        </Button>
      </div>

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div className="absolute z-10 mt-2">
          <Picker
            data={data}
            onEmojiSelect={(emoji: any) => {
              setContent(content + emoji.native);
              setShowEmojiPicker(false);
            }}
          />
        </div>
      )}

      {/* GIF Picker */}
      {showGifPicker && (
        <div className="absolute z-10 mt-2">
          <GifPicker
            onGifClick={handleGifSelect}
            tenorApiKey={import.meta.env.VITE_TENOR_API_KEY}
            autoFocusSearch={true}
            contentFilter={ContentFilter.HIGH}
          />
        </div>
      )}
    </div>
  );
}
